import React from 'react';
import { useRouter } from 'next/router';
import { GetServerSidePropsContext } from 'next';
import { Block, ButtonLink, Buttons, Container, createPage, IconFA, If, logErrorEvent, Slot } from 'react-commons';
import { faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';

import { useIsPremiumUser } from '@/lib/hooks/useIsPremiumUser';
import { useMetaProps } from '@/lib/hooks/useMetaProps';
import onlineGenres from '@/lib/data/onlineGenres.json';
import FluidLayout from '@/layouts/FluidLayout';
import AppHeader from '@/components/AppHeader';
import Meta, { generateTaxonomyBreadcrumbListLDJSON } from '@/components/Meta';
import { CharacterUpsellSlide, FeaturedCarouselProps } from '@/components/FeaturedCarousel';
import Pod from '@/components/Pod';
import CarouselPod from '@/components/pods/CarouselPod';
import FeaturedCarouselPod from '@/components/pods/FeaturedCarouselPod';
import GenresPod from '@/components/pods/GenresPod';
import SwGameRow from '@/components/SwGameRow';
import UpsellPod from '@/components/UpsellPod';
import OnlineGamesModel from '@/lib/drupal/models/OnlineGames';
import { CaptureClickEvent } from '@/lib/util/captureClickEvent';
import GamesModel, { FullGameData, ExtendedGameData } from '@/lib/drupal/models/Games';
import { ContentType, DailySearchConfig, DEFAULT_SORT_BY, searchForGames } from '@/lib/hooks/useDailyGamesByCriteria';
import NewGamesByTaxonomyPod from '@/components/pods/NewGamesByTaxonomyPod';
import UAParser from 'ua-parser-js';

import style from './index.module.scss';
import DownloadGamesModel from '@/lib/drupal/models/DownloadGames';

interface TaxonomyPageProps {
  featuredGames: FeaturedCarouselProps[ 'games' ]
  promotedGame: FullGameData
  topGames: ExtendedGameData[]
  games: ExtendedGameData[]
  criteria: DailySearchConfig
}

export async function getServerSideProps (context: GetServerSidePropsContext) {
  const params = context.params?.params as string[] ?? [];
  const query = context.query ?? {};
  
  let contentTypes: ContentType[] = [ 'paid_game', 'free_game' ];
  let genreArr = [];

  if (query[ 'tid[]' ]) {
    genreArr = Array.isArray(query[ 'tid[]' ]) 
      ? query[ 'tid[]' ] 
      : [ query[ 'tid[]' ] ];
  } else {
    if (Array.isArray(params)) {
      if (params.length === 1) {
        const found = onlineGenres.find(
          (onlineGenre) => onlineGenre.href.includes(params[ 0 ])
        );
        if (found) {
          genreArr = [ found.uid ];
        }
      } else {
        const found = onlineGenres.find(
          (onlineGenre) => onlineGenre.href.includes(params[ 1 ])
        );
        if (found) {
          genreArr = [ found.uid ];
        }
      }
    } else {
      genreArr = params ? params : [];
    }
  }

  if (query[ 'content_type[]' ]) {
    contentTypes = (Array.isArray(query[ 'content_type[]' ]) 
      ? query[ 'content_type[]' ] 
      : [ query[ 'content_type[]' ] ]) as ContentType[];
  }

  switch (params[ 0 ]) {
  case 'daily-games':
  case 'free-games':
    if(!contentTypes.includes('free_game')) {
      contentTypes.push('free_game');
    }
    break;
  case 'paid-games':
    if(!contentTypes.includes('paid_game')) {
      contentTypes.push('paid_game');
    }
    break;
  }

  const isDownloadGame = contentTypes.includes('paid_game');

  const genres = genreArr.length 
    ? genreArr.map(
      (genre) => onlineGenres
        .find((onlineGenre) => onlineGenre.uid === genre)
        .uid
    )
    : [ '0' ];

  const genre = onlineGenres.find((genre) => genre.uid === genreArr[ 0 ]);

  const criteria: DailySearchConfig = {
    query: query.title as string || '',
    genres,
    sortBy: query.sortBy as any || DEFAULT_SORT_BY,
    contentTypes: contentTypes,
  };

  let featuredGames = [];
  if (genre) {
    try {
      featuredGames = isDownloadGame
        ? await DownloadGamesModel.getSliderGamesForGenre(genre, 4, 0)
        : await OnlineGamesModel.getSliderGamesForGenre(genre, 4, 0);
    } catch (err) {
      throw err;
    }
  }

  let promotedGamePartial: ExtendedGameData[] = [];
  if (genre) {
    try {
      promotedGamePartial = isDownloadGame
        ? await DownloadGamesModel.getFeaturedGamesForGenre(genre, 1, 0)
        : await OnlineGamesModel.getFeaturedGamesForGenre(genre, 1, 0);
    } catch (err) {
      throw err;
    }
  }

  let promotedGame: FullGameData = null;
  if (genre) {
    try {
      promotedGame = await GamesModel.getBySlug(promotedGamePartial[ 0 ].href);
      promotedGame.description = promotedGamePartial[ 0 ].description;
    } catch (err) {
      logErrorEvent('Online Taxonomy Page: Featured Game Error', true, err);
      promotedGame = promotedGamePartial[ 0 ] as never as FullGameData;
    }
  }

  let topGames = [];
  if (genre) {
    try {
      topGames = isDownloadGame
        ? await DownloadGamesModel.getTopGamesForGenre(genre, 4, 0)
        : await OnlineGamesModel.getTopGamesForGenre(genre, 4, 0);
    } catch (err) {
      throw err;
    }
  }

  const parser = new UAParser(context.req.headers[ 'user-agent' ] || '');
  const parserResults = parser.getResult();
  const mobileOnly = parserResults.device.type === 'mobile';

  let games;
  try {
    games = await searchForGames({
      ...criteria,
      method: (
        limit: number, 
        offset: number, 
        criteria: Omit<DailySearchConfig, 'method'>, 
        sortOrder: 'ASC' | 'DESC', 
      ) => {
        return GamesModel.dailySearchAllGames(
          limit, 
          offset, 
          criteria, 
          sortOrder, 
          mobileOnly
        );
      }
    });
  } catch (err) {
    throw err;
  }

  const props: TaxonomyPageProps = {
    featuredGames: featuredGames as any,
    promotedGame: promotedGame,
    topGames,
    games: games.games,
    criteria,
  };

  return {
    props,
  };
}

export default createPage<TaxonomyPageProps>('ClassicSearchPage', { style }, function SearchPage ({ className }, props) {
  const [ isPremiumUser ] = useIsPremiumUser();
  const router = useRouter();
  const currentGenre = onlineGenres.find((genre) => genre.uid === props.criteria.genres[ 0 ]);

  const metaProps = useMetaProps(!currentGenre
    ? {
      title: 'Games | FREE Online Games & Download Games | Play Games on Shockwave.com',
      description: 'Shockwave is the ultimate destination to play free online games.',
      keywords: 'free online games, fun online games, play online games',
    } 
    : (
      props.criteria.contentTypes[ 0 ] === 'paid_game' 
        ? {
          title: `Download ${currentGenre.name} Games | Free ${currentGenre.name} Games | Shockwave.com`,
          description: `Download ${currentGenre.name} Games and get your game on! Find free downloadable ${currentGenre.name} games on Shockwave.com.`,
          ldjson: [
            ...generateTaxonomyBreadcrumbListLDJSON(
              currentGenre.name, 
              `${process.env.APP_CONFIG.URL}/images/genreIcons/${currentGenre.icon}.svg`,
              `Download ${currentGenre.name} Games and get your game on! Find free downloadable ${currentGenre.name} games on Shockwave.com.`, 
              router.asPath, 
              [ { name: `${currentGenre.name} Games`, url: router.asPath } ]
            ),
          ]
        } 
        : {
          title: `${currentGenre.name} Games | Free Online ${currentGenre.name} Games | Shockwave.com`,
          description: `${currentGenre.name} games to play online, free ${currentGenre.name} games and more at Shockwave.com`,
          ldjson: [
            ...generateTaxonomyBreadcrumbListLDJSON(
              currentGenre.name, 
              `${process.env.APP_CONFIG.URL}/images/genreIcons/${currentGenre.icon}.svg`,
              `${currentGenre.name} games to play online, free ${currentGenre.name} games and more at Shockwave.com`, 
              router.asPath, 
              [ { name: `${currentGenre.name} Games`, url: router.asPath } ]
            ),
          ]
        }
    ));

  return (
    <FluidLayout
      useAdColumn
      classicColumnWidth
      className={className}
      pageName='search_classic'
      key={router.asPath}
    >
      <Slot name='appHeader'>
        <AppHeader />
      </Slot>

      {/* Meta information */}
      <Meta 
        {...metaProps}
        sitemapProps={{
          priority: '1.0',
          changefreq: 'daily',
        }}
      />

      <Container>
        <Block>
        
          {
            If(props.featuredGames.length > 0, () => (
              <FeaturedCarouselPod
                h1
                games={props.featuredGames}
                useUpsellSlide={
                  <CaptureClickEvent
                    action='upsell'
                    className='upsell_taxonomy_featured_game'
                    location='featured_game_panel_online'
                    href='/unlimited'
                  >
                    <CharacterUpsellSlide 
                      className='upsell_taxonomy_featured_game'
                    />
                  </CaptureClickEvent>
                }
                upsellSlidePosition={3}
              >
                <Slot name='title'>{currentGenre.name} Games</Slot>
              </FeaturedCarouselPod>
            )).EndIf()
          }

          {
            If(props.promotedGame, () => (
              <Pod>
                <Slot name='title'>Featured {currentGenre.name} Game</Slot>
                <SwGameRow 
                  title={props.promotedGame.title}
                  description={props.promotedGame.description}
                  publishedAt={props.promotedGame.publishedAt}
                  href={props.promotedGame.href}
                  rating={props.promotedGame.rating}
                  numRatings={props.promotedGame.numRatings}
                  numPlays={props.promotedGame.numPlays} 
                  thumbnailImg={props.promotedGame.thumbnailImg}
                />
              </Pod>
            )).EndIf()
          }

          {
            If(props.topGames.length > 0, () => (
              <CarouselPod
                initialGames={props.topGames}
                hideButtons
                tallImg
              >
                <Slot name='title'>Top {currentGenre.name} Games</Slot>
                <Slot name='footer'>
                  <Buttons>
                    <ButtonLink href={`/online/all-games?genre=${currentGenre.uid}`} secondary small className='--iconRight'>
                      See All {currentGenre.name} Games
                      <IconFA icon={faCircleArrowRight} />
                    </ButtonLink>
                  </Buttons>
                </Slot>
              </CarouselPod>
            )).EndIf()
          }

          <NewGamesByTaxonomyPod
            key={router.asPath}
            initialGames={props.games}
            initialCriteria={props.criteria}
          >
            <Slot name='title'>
              {
                currentGenre?.name || (
                  props.criteria.contentTypes.includes('paid_game')
                    ? 'All Download'
                    : 'All Online'
                )
              } Games
            </Slot>
          </NewGamesByTaxonomyPod>

          {
            If(!isPremiumUser, () => (
              <CaptureClickEvent
                action='upsell'
                className='upsell_taxonomy_swu_promo'
                location='swu_panel_upsell_taxonomy_online'
                href='/unlimited'
              >
                <UpsellPod className='upsell_taxonomy_swu_promo' />
              </CaptureClickEvent>
            )).EndIf()
          }

          <GenresPod />

        </Block>
      </Container>
      
    </FluidLayout>
  );
});
